import { useState } from "react";
import { toast } from "sonner";
import { Truck, Dumbbell, Clock, HeartHandshake, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { Field, Chip, FormSection, SuccessPanel } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

const HELP_TYPES = [
  "Truck or trailer for pickups",
  "Muscle — lifting & moving furniture",
  "Time for pickups & deliveries",
  "Cleaning & preparing items",
  "Minor furniture repairs",
  "Organizing storage",
  "Other skills",
];

const WAYS = [
  { icon: Truck, title: "Trucks & Trailers", text: "Have a truck or trailer? Help us pick up donated furniture and deliver it to families." },
  { icon: Dumbbell, title: "Muscle", text: "Couches don't move themselves. Lend your strength on pickup and delivery days." },
  { icon: Clock, title: "Time", text: "A few hours on a Saturday can furnish an entire home for a family starting over." },
  { icon: HeartHandshake, title: "Skills", text: "Cleaning, minor repairs, organizing storage — every skill helps the next family." },
];

export default function Volunteers() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", zip_code: "",
    help_types: [], availability: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (val) =>
    setForm({
      ...form,
      help_types: form.help_types.includes(val)
        ? form.help_types.filter((v) => v !== val)
        : [...form.help_types, val],
    });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="volunteers-page">
      <PageHero overline="Volunteer" title="LEND A TRUCK," italic="a hand, or an hour." testid="volunteers-headline">
        Fresh Start Furnishings runs on neighbors helping neighbors. Offer your truck, your muscle, or your time —
        and help turn empty houses into homes across Glendale and the West Valley.
      </PageHero>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        {done ? (
          <SuccessPanel
            testid="volunteer-success-message"
            title="THANK YOU FOR STEPPING UP!"
            message="Your volunteer signup has been sent to our team. We'll reach out to coordinate pickups, deliveries, and volunteer days."
          />
        ) : (
          <div className="grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <h2 className="font-serif text-2xl text-olive sm:text-3xl">Ways to help</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {WAYS.map((w, i) => (
                  <div key={w.title} data-testid={`volunteer-way-${i}`} className="bento-card !p-5">
                    <w.icon className="h-7 w-7 text-burgundy" />
                    <h3 className="mt-3 font-serif text-lg text-olive">{w.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-moss">{w.text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 border-l-2 border-burgundy pl-5 font-serif text-lg italic leading-relaxed text-olive">
                "One Saturday morning. One borrowed truck. One family eating dinner at a real table."
              </p>
            </FadeUp>

            <FadeUp delay={0.15}>
              <form onSubmit={submit} className="space-y-6 rounded-2xl border border-linen bg-white/60 p-8" data-testid="volunteer-form">
                <FormSection number="01" title="Your Information">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Full name" required testid="volunteer-name-field">
                      <input data-testid="volunteer-name-input" required className="input-field" value={form.name} onChange={set("name")} />
                    </Field>
                    <Field label="Phone number" required testid="volunteer-phone-field">
                      <input data-testid="volunteer-phone-input" required type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
                    </Field>
                    <Field label="Email" required testid="volunteer-email-field">
                      <input data-testid="volunteer-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} />
                    </Field>
                    <Field label="City" testid="volunteer-city-field">
                      <input data-testid="volunteer-city-input" className="input-field" value={form.city} onChange={set("city")} />
                    </Field>
                    <Field label="ZIP code" testid="volunteer-zip-field">
                      <input data-testid="volunteer-zip-input" className="input-field" maxLength={5} value={form.zip_code} onChange={set("zip_code")} />
                    </Field>
                    <Field label="Availability" testid="volunteer-availability-field">
                      <select data-testid="volunteer-availability-select" className="input-field" value={form.availability} onChange={set("availability")}>
                        <option value="">Select…</option>
                        <option>Weekday mornings</option>
                        <option>Weekday afternoons</option>
                        <option>Weekday evenings</option>
                        <option>Saturdays</option>
                        <option>Sundays</option>
                        <option>Flexible / on call</option>
                      </select>
                    </Field>
                  </div>
                </FormSection>

                <FormSection number="02" title="How Can You Help?">
                  <div className="flex flex-wrap gap-2">
                    {HELP_TYPES.map((t) => (
                      <Chip key={t} testid={`help-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                        checked={form.help_types.includes(t)} onToggle={() => toggle(t)} />
                    ))}
                  </div>
                  <Field label="Anything else we should know?" testid="volunteer-message-field">
                    <textarea data-testid="volunteer-message-input" rows={3} className="input-field"
                      placeholder="Vehicle type, experience, questions…" value={form.message} onChange={set("message")} />
                  </Field>
                </FormSection>

                <button type="submit" data-testid="volunteer-form-submit-button" disabled={submitting}
                  className="btn-accent w-full !py-4 disabled:opacity-60">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {submitting ? "Signing up…" : "Sign Up to Volunteer"}
                </button>
              </form>
            </FadeUp>
          </div>
        )}
      </section>
    </div>
  );
}
